import React, { useState } from "react";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaFileExcel,
  FaInfoCircle,
  FaTrash,
} from "react-icons/fa";

import BackButton from "../components/common/BackButton";
import Loader from "../components/common/Loader";
import ToolInfoModal from "../components/common/ToolInfoModal";
import { useToast } from "../context/ToastContext";
import reportsApi from "../services/reportsApi";
import { extractErrorMessage } from "../utils/errorUtils";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import "./EmployeeSpiffReportTool.css";

function EmployeeSpiffReportTool() {
  const [spiffFile, setSpiffFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Employee Spiff Report",
    description: "Upload a Spiff Excel file to generate the processed report.",
    steps: [
      "Upload your Spiff Excel file.",
      "Click 'Generate Spiff Report' to process and download the output.",
    ],
    templates: [],
  };

  const guideTips = [
    "Use the most recent spiff sales workbook so the report reflects the latest numbers for the team.",
    "Only Excel files are supported for this flow.",
    "The output is generated by the reports service, so processing can take a little longer than a simple download.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!spiffFile) {
      addToast("Please upload a Spiff Excel file.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (spiffFile.size > maxSize) {
      addToast("The file is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("spiff_sales", spiffFile);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await reportsApi.post("/performance/spiff", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Employee_Spiff_Report_Output.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File processed and downloaded successfully.", "success");
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Error generating spiff report.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const renderProgress = () => {
    if (!loading) {
      return null;
    }

    if (uploadProgress < 100) {
      return (
        <div className="esrt-progress-panel">
          <div className="esrt-progress-bar-track">
            <div
              className="esrt-progress-bar-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="esrt-progress-copy">
            Uploading workbook to the reports service... {uploadProgress}%
          </p>
        </div>
      );
    }

    return (
      <div className="esrt-progress-panel esrt-progress-panel-loading">
        <Loader message="Processing Spiff Report..." />
      </div>
    );
  };

  return (
    <>
      <div className="esrt-container">
        <div className="esrt-topbar">
          <BackButton label="Back to HR Tools" />
          <button
            type="button"
            className="esrt-info-button"
            onClick={() => setShowInfoModal(true)}
          >
            <FaInfoCircle /> Tool Info
          </button>
        </div>

        <section className="esrt-header-card">
          <h1 className="esrt-title">Employee Spiff Report</h1>
        </section>

        <div className="esrt-layout">
          <section className="esrt-card esrt-main-card">
            <form onSubmit={handleSubmit} className="esrt-form">
              <div className="esrt-field-stack">
                <label className="esrt-label">Spiff Workbook</label>
                <div className={`esrt-upload-zone ${spiffFile ? "has-file" : ""}`}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="esrt-upload-input"
                    onClick={(event) => {
                      event.target.value = null;
                    }}
                    onChange={(event) => setSpiffFile(event.target.files?.[0] || null)}
                  />
                  <div className="esrt-upload-content">
                    {spiffFile ? (
                      <>
                        <FaCheckCircle className="esrt-upload-icon" />
                        <div className="esrt-upload-text-block">
                          <strong>{spiffFile.name}</strong>
                          <span>Workbook attached and ready to process</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <FaFileExcel className="esrt-upload-icon" />
                        <div className="esrt-upload-text-block">
                          <strong>Choose spiff workbook</strong>
                          <span>Excel files only, up to 100MB</span>
                        </div>
                        <span className="esrt-upload-action">
                          <FaCloudUploadAlt /> Browse
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="esrt-file-footer">
                {spiffFile ? (
                  <button
                    type="button"
                    className="esrt-clear-button"
                    onClick={() => setSpiffFile(null)}
                  >
                    <FaTrash /> Clear file
                  </button>
                ) : null}
              </div>

              <button
                type="submit"
                className="esrt-primary-button"
                disabled={loading || !spiffFile}
              >
                {loading ? "Processing..." : "Generate Spiff Report"}
              </button>
            </form>

            {renderProgress()}
          </section>
        </div>
      </div>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default EmployeeSpiffReportTool;
